import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import "./ApplicationLogs.css";
import { RefreshCw, Search, FileText, User, Activity } from "lucide-react";
import ScrollToTop from "../../../Ui/UiJs/ScrollToTop";

import {
    getApplicationLogsRequest,
} from "../../../redux/actions/applicationLogActions";

const STATUS_CONFIG = {
    SUCCESS: {
        label: "Succès",
        color: "#166534",
        bg: "#dcfce7",
    },
    FAILED: {
        label: "Échec",
        color: "#b91c1c",
        bg: "#fee2e2",
    },
};

const formatDateTime = (value) => {
    if (!value) return "—";
    try {
        const date = new Date(value);
        return date.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
};

const ApplicationLogs = ({ logs, loading, error, getApplicationLogs }) => {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [moduleFilter, setModuleFilter] = useState("ALL");

    useEffect(() => {
        getApplicationLogs();
    }, [getApplicationLogs]);

    const modules = useMemo(() => {
        const list = (logs || [])
            .map((log) => log.module)
            .filter(Boolean);

        return [...new Set(list)].sort();
    }, [logs]);

    const filteredLogs = useMemo(() => {
        const term = search.trim().toLowerCase();

        return (logs || []).filter((log) => {
            const matchesSearch =
                !term ||
                (log.module || "").toLowerCase().includes(term) ||
                (log.action || "").toLowerCase().includes(term) ||
                (log.entityName || "").toLowerCase().includes(term) ||
                String(log.entityId || "").toLowerCase().includes(term) ||
                (log.description || "").toLowerCase().includes(term) ||
                (log.username || "").toLowerCase().includes(term) ||
                (log.fullName || "").toLowerCase().includes(term) ||
                (log.errorMessage || "").toLowerCase().includes(term);

            const matchesStatus =
                statusFilter === "ALL" || log.status === statusFilter;

            const matchesModule =
                moduleFilter === "ALL" || log.module === moduleFilter;

            return matchesSearch && matchesStatus && matchesModule;
        });
    }, [logs, search, statusFilter, moduleFilter]);

    return (
        <div className="application-logs-page">
            {/* ── Header ─────────────────────────── */}
            <div className="application-logs-header">
                <div>
                    <h3>Consultez les actions effectuées dans l’application.</h3>
                    {/*<p className="al-subtitle">Consultez les actions effectuées dans l’application.</p>*/}
                </div>
                <div className="application-logs-header-actions">
                    <button className="al-btn al-btn-primary" onClick={getApplicationLogs} disabled={loading}>
                        <RefreshCw size={14} className={loading ? "al-spin" : ""} />
                        {loading ? "Chargement..." : "Actualiser"}
                    </button>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────── */}
            <div className="application-logs-toolbar">
                <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
                    <Search size={14} style={{ position: "absolute", left: 12, color: "#6e91c1" }} />
                    <input
                        type="text"
                        className="application-logs-search"
                        style={{ paddingLeft: 34 }}
                        placeholder="Rechercher par module, action, utilisateur, description..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <select
                    className="application-logs-filter"
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                >
                    <option value="ALL">Tous les modules</option>
                    {modules.map((module) => (
                        <option key={module} value={module}>
                            {module}
                        </option>
                    ))}
                </select>

                <select
                    className="application-logs-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="ALL">Tous les statuts</option>
                    <option value="SUCCESS">Succès</option>
                    <option value="FAILED">Échec</option>
                </select>

                <div className="application-logs-count">
                    {filteredLogs.length} résultat{filteredLogs.length > 1 ? "s" : ""}
                </div>
            </div>

            {error && <div className="application-logs-alert error">{error}</div>}

            {/* ── Table ──────────────────────────── */}
            <div className="application-logs-table-wrapper">
                {loading ? (
                    <div className="al-loading">
                        <div className="al-spinner" />
                        <span>Chargement des logs...</span>
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="al-empty">Aucun log applicatif trouvé</div>
                ) : (
                    <table className="application-logs-table">
                        <thead>
                        <tr>
                            <th>Date & heure</th>
                            <th>Module</th>
                            <th>Action</th>
                            <th>Entité</th>
                            <th>Statut</th>
                            <th>Utilisateur</th>
                            <th>Description</th>
                            <th>Ressources</th>
                        </tr>
                        </thead>

                        <tbody>
                        {filteredLogs.map((log) => {
                            const statusCfg = STATUS_CONFIG[log.status] || {
                                label: log.status || "—",
                                color: "#334155",
                                bg: "#e2e8f0",
                            };

                            return (
                                <tr key={log.id}>
                                    <td className="al-date">{formatDateTime(log.createdAt)}</td>
                                    <td><span className="al-code">{log.module || "—"}</span></td>
                                    <td className="al-name">{log.action || "—"}</td>
                                    <td className="al-muted">
                                        {log.entityName ? (
                                            <>
                                                {log.entityName} <small style={{ color: "#b8c3d1" }}>(ID: {log.entityId})</small>
                                            </>
                                        ) : "—"}
                                    </td>
                                    <td>
                                        <span
                                            className="application-logs-status"
                                            style={{
                                                color: statusCfg.color,
                                                background: statusCfg.bg,
                                            }}
                                        >
                                            {statusCfg.label}
                                        </span>
                                    </td>
                                    <td className="al-name" style={{ color: "#6e91c1" }}>
                                        {log.fullName || log.username || "—"}
                                    </td>
                                    <td className="application-logs-description">
                                        {log.description || "—"}
                                        {log.errorMessage && (
                                            <div className="application-logs-error-text" style={{ marginTop: 4 }}>
                                                {log.errorMessage}
                                            </div>
                                        )}
                                    </td>
                                    <td className="application-logs-useragent">{log.userAgent || "—"}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>
            <ScrollToTop />
        </div>
    );
};

const mapStateToProps = (state) => ({
    logs: state.applicationLog?.logs || [],
    loading: state.applicationLog?.loading || false,
    error: state.applicationLog?.error || null,
});

const mapDispatchToProps = (dispatch) => ({
    getApplicationLogs: () => dispatch(getApplicationLogsRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationLogs);